import { Component, Input, OnInit, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { MatDialog } from "@angular/material/dialog";
import { ResourceEditorComponent } from "../resource-editor/resource-editor.component";
import { ResourceStore } from "@app/services/store/resource-store";
import { routeAnimations } from "@app/core";
import { BaseDataTablePageDirective } from "@app/view/shared/base-pages/base-data-table-page/base-data-table-page.directive";
import { SystemSearchMetaRestService } from "@app/services/hal/system-search-meta-rest.service";
import { UntypedFormBuilder } from "@angular/forms";
import { Router } from "@angular/router";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { ResourceModel, WorkMode } from "@app/models/rest/resource";
import { TeamStore } from "@app/services/store/team-store";
import { FilterConfig } from "@bryntum/grid-thin";
import {
  CollectionFilterConfig,
  FieldFilterPickerConfig,
  Store as BryntumStore
} from "@bryntum/core-thin";
import { MatToolbar } from "@angular/material/toolbar";
import { MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { GridComponent } from "../../../../shared/base-pages/base-data-table-page/grid.component";
import { MatPaginator } from "@angular/material/paginator";
import { TranslatePipe } from "@ngx-translate/core";
import { DataImportService } from "@app/services/data-import/data-import.service";
import { DataImportWizardComponent } from "@app/view/setup/data-import/data-import-wizard/data-import-wizard.component";
import { MatTooltip } from "@angular/material/tooltip";

@Component({
  selector: "app-resource-list",
  templateUrl: "./resource-list.component.html",
  styleUrls: ["./resource-list.component.scss"],
  animations: [routeAnimations],
  imports: [
    MatToolbar,
    MatIconButton,
    MatIcon,
    GridComponent,
    MatPaginator,
    TranslatePipe,
    MatTooltip
  ]
})
export class ResourceListComponent
  extends BaseDataTablePageDirective<ResourceModel>
  implements OnInit
{
  resourceStore: ResourceStore;
  protected store: Store<any>;
  protected dialog: MatDialog;
  protected formBuilder: UntypedFormBuilder;
  protected router = inject(Router);
  serverErrorHandler: ServiceErrorHandlerService;
  protected ngxUiLoaderService: NgxUiLoaderService;
  private dataImportService = inject(DataImportService);
  private teamStore = inject(TeamStore);

  // The grid store only holds the current page, so the filter picker collects
  // its multiselect options from this store, populated with the full set of
  // resource and team names in ngOnInit (same pattern as the data views).
  filterValuesStore = new BryntumStore({
    fields: ["id", "name"],
    data: []
  });

  filterConfig: FilterConfig = {
    pickerConfig: {
      showAddFilterButton: false,
      getFieldFilterPickerConfig: (
        collectionFilterConfig: CollectionFilterConfig
      ): Partial<FieldFilterPickerConfig> => {
        switch (collectionFilterConfig.property) {
          case "name":
          case "team.name": {
            const fixedFieldResult: any = {
              operatorLocked: true,
              propertyLocked: true,
              store: this.filterValuesStore,
              operators: this.fixedSetOperators,
              // The picker's multiselect combo hardcodes createOnUnmatched,
              // which offers to add unknown names as new values while typing
              getValueFieldConfig: (filter, fieldConfig) => {
                fieldConfig.createOnUnmatched = false;
                return fieldConfig;
              }
            };
            if (
              this.gridStore.filters.findItem(
                "property",
                collectionFilterConfig.property
              ) === null
            ) {
              fixedFieldResult.filter = {
                id: `${collectionFilterConfig.property}-isIncludedIn`,
                property: collectionFilterConfig.property,
                operator: "isIncludedIn",
                value: collectionFilterConfig.value,
                disabled: collectionFilterConfig.disabled
              };
            }
            return fixedFieldResult;
          }
          case "workMode": {
            const workModeResult: any = {
              operatorLocked: true,
              propertyLocked: true,
              operators: {
                string: [
                  {
                    value: "=",
                    text: "equals",
                    argCount: 1
                  }
                ]
              },
              getValueFieldConfig: (filter, fieldConfig) => {
                fieldConfig.type = "combo";
                fieldConfig.items = [WorkMode.Online, WorkMode.Offline];
                fieldConfig.editable = false;
                return fieldConfig;
              }
            };
            if (
              this.gridStore.filters.findItem("property", "workMode") === null
            ) {
              workModeResult.filter = {
                id: "workMode-equals",
                property: "workMode",
                operator: "=",
                value: collectionFilterConfig.value,
                disabled: collectionFilterConfig.disabled
              };
            }
            return workModeResult;
          }
        }
        return {};
      }
    }
  };

  @Input()
  public columns: object[] = [
    // {
    //   field: 'id',
    //   text: 'ID',
    //   flex: 1,
    //   editor: false
    // },
    {
      width: 60,
      type: "template",
      field: "imageUrl",
      template: ({ record }) => {
        if (record.imageUrl) {
          return `<dl style="margin:0">
                  <dt><img src="${record.imageUrl}" height="40" width="40" style="border-radius:100%;box-shadow:1px 0px 5px #aaa"/></dt>
                  </dl>`;
        } else {
          // Create initials fallback
          const initials = this.getInitials(record.name);
          return `<dl style="margin:0">
                  <dt><div style="display:flex;justify-content:center;align-items:center;height:40px;width:40px;background-color:#3f51b5;color:white;border-radius:100%;box-shadow:1px 0px 5px #aaa;font-weight:bold;">${initials}</div></dt>
                  </dl>`;
        }
      },
      editor: false
    },
    {
      field: "name",
      text: "Name",
      flex: 1,
      editor: true
    },
    {
      field: "team.name",
      text: "Team",
      flex: 1,
      editor: false,
      sortable: true
    },
    {
      field: "workMode",
      text: "Work Mode",
      flex: 1,
      editor: {
        type: "dropdown",
        items: ["Online", "Offline"],
        editable: false
      }
    },
    {
      type: "action",
      text: "Manage",
      width: 70,
      editor: false,
      actions: [
        {
          cls: "fa fa-chevron-right",
          tooltip: "Manage",
          onClick: ({ record }) => {
            if (isNaN(record.id)) {
              console.log("unsaved");
              this.serverErrorHandler.handleClientError(
                "Save changes before editing this record"
              );
              return;
            }
            this.openResource(record.id);
          }
        }
      ]
    }
  ];

  constructor() {
    const systemSearchMetaRestService = inject(SystemSearchMetaRestService);
    const resourceStore = inject(ResourceStore);
    const store = inject<Store<any>>(Store);
    const dialog = inject(MatDialog);
    const formBuilder = inject(UntypedFormBuilder);
    const serverErrorHandler = inject(ServiceErrorHandlerService);
    const ngxUiLoaderService = inject(NgxUiLoaderService);

    super(
      systemSearchMetaRestService,
      resourceStore,
      store,
      dialog,
      "Resource",
      formBuilder,
      serverErrorHandler,
      ngxUiLoaderService
    );

    this.resourceStore = resourceStore;
    this.store = store;
    this.dialog = dialog;
    this.formBuilder = formBuilder;
    this.serverErrorHandler = serverErrorHandler;
    this.ngxUiLoaderService = ngxUiLoaderService;
  }

  public get pristine() {
    return (
      !this.gridStore.changes ||
      (this.gridStore.changes.added.length === 0 &&
        this.gridStore.changes.removed.length === 0 &&
        this.gridStore.changes.modified.length === 0)
    );
  }

  ngOnInit() {
    super.ngOnInit();
    this.gridStore.autoCommit = false;
    this.gridStore.load({});
    this.loadFilterValues();
  }

  // ngOnInit is re-invoked after the import wizard / new resource dialog
  // close, so replace the store contents wholesale rather than appending
  private loadFilterValues() {
    Promise.all([this.resourceStore.loadAll(), this.teamStore.loadAll()]).then(
      ([resourcesResult, teamsResult]) => {
        const filterValues = [
          ...resourcesResult.resources.map((resource, index) => ({
            id: index + 1000,
            name: resource.name
          })),
          ...teamsResult.resources.map((team, index) => ({
            id: index + 2000,
            team: { name: team.name }
          }))
        ];
        this.filterValuesStore.removeAll();
        if (filterValues.length > 0) {
          this.filterValuesStore.add(filterValues);
        }
      }
    );
  }

  addRow() {
    this.gridStore.insert(0, {
      code: "",
      description: "",
      scoreWeight: 1
    });
  }

  openResource(id) {
    console.log("opening resource with id " + id);
    this.router.navigate(["setup/resourcing/resources", id]).then();
  }

  openNewResourceDialog(): void {
    const dialogRef = this.dialog.open(ResourceEditorComponent);
    //   , {
    //   width: '350px',
    //   data: { mode: 'NEW' }
    // });

    dialogRef.afterClosed().subscribe((result) => {
      console.log("The dialog was closed");
      this.ngOnInit();
      // console.log(result);
    });
  }

  // Extract initials from a name (e.g., "John Doe" -> "JD")
  getInitials(name: string): string {
    if (!name) return "";

    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .substring(0, 2);
  }

  /**
   * Open the data import wizard dialog
   */
  openImportWizard(): void {
    const dialogRef = this.dialog.open(DataImportWizardComponent, {
      width: "800px",
      maxHeight: "90vh",
      data: { entityType: "RESOURCE" }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Refresh the grid if import was successful
        this.ngOnInit();
      }
    });
  }

  /**
   * Export resources directly to Excel
   */
  async exportToExcel(): Promise<void> {
    try {
      await this.dataImportService.exportResourcesToExcel();
    } catch (error) {
      console.error("Export error:", error);
      this.serverErrorHandler.handleClientError("Failed to export resources");
    }
  }
}
